import React, { useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import Loader from "../../../../components/Loader.jsx";

ChartJS.register(ArcElement, Tooltip, Legend);

const GET_COURSE_DASHBOARD_DETAILS = gql`
  query GetCourseDashboardDetails(
    $course_id: String!
    $student_id: String!
    $campus_name: String!
  ) {
    getCourseDashboardDetails(
      course_id: $course_id
      student_id: $student_id
      campus_name: $campus_name
    ) {
      inProgressCount
      completedCount
      awaitingCount
      categories {
        completedCount
        categoryName
        inProgressCount
        awaitingCount
      }
    }
  }
`;

const STATUS_META = [
  {
    key: "completed",
    label: "Completed",
    field: "completedCount",
    dot: "bg-emerald-500",
    valueClass: "text-emerald-700",
    chart: "#059669",
  },
  {
    key: "inProgress",
    label: "In progress",
    field: "inProgressCount",
    dot: "bg-sky-500",
    valueClass: "text-sky-700",
    chart: "#0284c7",
  },
  {
    key: "awaiting",
    label: "Awaiting",
    field: "awaitingCount",
    dot: "bg-amber-500",
    valueClass: "text-amber-700",
    chart: "#d97706",
  },
];

function pickCounts(row) {
  return {
    completed: row?.completedCount ?? 0,
    inProgress: row?.inProgressCount ?? 0,
    awaiting: row?.awaitingCount ?? 0,
  };
}

function totalFromCounts({ completed, inProgress, awaiting }) {
  return completed + inProgress + awaiting;
}

function pct(part, whole) {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

const CourseDashboardCard = ({ courseId, studentId, campusName }) => {
  const { data, loading, error } = useQuery(GET_COURSE_DASHBOARD_DETAILS, {
    variables: {
      course_id: courseId,
      student_id: studentId,
      campus_name: campusName,
    },
    skip: !courseId || !studentId || !campusName,
    fetchPolicy: "network-only",
  });

  const details = data?.getCourseDashboardDetails;
  const overview = useMemo(() => pickCounts(details), [details]);
  const total = useMemo(() => totalFromCounts(overview), [overview]);

  const chartData = useMemo(
    () => ({
      labels: STATUS_META.map((s) => s.label),
      datasets: [
        {
          data: [
            overview.completed,
            overview.inProgress,
            overview.awaiting,
          ],
          backgroundColor: STATUS_META.map((s) => s.chart),
          borderWidth: 0,
          hoverOffset: 6,
        },
      ],
    }),
    [overview]
  );

  const chartOptions = useMemo(
    () => ({
      cutout: "72%",
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.raw ?? 0;
              const p = pct(v, total);
              return ` ${ctx.label}: ${v} (${p}%)`;
            },
          },
        },
      },
      maintainAspectRatio: false,
    }),
    [total]
  );

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center py-8">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <p className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
        Could not load progress data.
      </p>
    );
  }

  if (!details) return null;

  const categories = details.categories ?? [];
  const completionPct = pct(overview.completed, total);

  return (
    <div className="space-y-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <div className="flex min-h-[88px] flex-col justify-center rounded-lg border border-slate-200 px-3 py-2.5">
          <div className="mb-1 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-slate-400" />
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Total Qs
            </span>
          </div>
          <p className="text-xl font-bold tabular-nums text-slate-700">{total}</p>
          <p className="mt-0.5 text-[10px] text-slate-400">All questions</p>
        </div>

        {STATUS_META.map(({ key, label, field, dot, valueClass }) => (
          <div
            key={key}
            className="flex min-h-[88px] flex-col justify-center rounded-lg border border-slate-200 px-3 py-2.5"
          >
            <div className="mb-1 flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${dot}`} />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                {label}
              </span>
            </div>
            <p className={`text-xl font-bold tabular-nums ${valueClass}`}>
              {details[field] ?? 0}
            </p>
            <p className="mt-0.5 text-[10px] text-slate-400">
              {pct(details[field] ?? 0, total)}% of total
            </p>
          </div>
        ))}

        <div className="col-span-2 flex min-h-[88px] flex-col items-center justify-center rounded-lg border border-slate-200 px-2 py-2 sm:col-span-1">
          <div className="relative h-[72px] w-full max-w-[88px]">
            {total > 0 ? (
              <>
                <Doughnut data={chartData} options={chartOptions} />
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-base font-bold tabular-nums text-slate-800">
                    {total}
                  </span>
                  <span className="text-[9px] font-medium uppercase tracking-wide text-slate-500">
                    Total
                  </span>
                </div>
              </>
            ) : (
              <div className="flex h-full items-center justify-center text-center text-[10px] text-slate-400">
                No data
              </div>
            )}
          </div>
          <p className="mt-1 text-center text-[10px] text-slate-500">
            <span className="font-semibold text-emerald-700">{completionPct}%</span>{" "}
            done
          </p>
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-800">
            Category breakdown
          </h3>
          <span className="text-xs text-slate-500">
            {categories.length} categor{categories.length === 1 ? "y" : "ies"}
          </span>
        </div>

        {categories.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-200 px-4 py-8 text-center text-sm text-slate-500">
            No categories in this course.
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/90">
                  <th className="px-4 py-3 font-semibold text-slate-700">
                    Category
                  </th>
                  <th className="hidden px-3 py-3 text-center font-semibold text-emerald-700 sm:table-cell">
                    Completed
                  </th>
                  <th className="hidden px-3 py-3 text-center font-semibold text-sky-700 sm:table-cell">
                    Active
                  </th>
                  <th className="hidden px-3 py-3 text-center font-semibold text-amber-800 sm:table-cell">
                    Pending
                  </th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700">
                    Completion %
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {categories.map((cat) => {
                  const counts = pickCounts(cat);
                  const catTotal = totalFromCounts(counts);
                  const donePct = pct(counts.completed, catTotal);
                  return (
                    <tr
                      key={cat.categoryName}
                      className="transition-colors hover:bg-slate-50/60"
                    >
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {cat.categoryName}
                        <div className="mt-1 flex flex-wrap gap-2 sm:hidden">
                          <span className="text-[10px] font-semibold text-emerald-700">
                            {counts.completed} completed
                          </span>
                          <span className="text-[10px] font-semibold text-sky-700">
                            {counts.inProgress} active
                          </span>
                          <span className="text-[10px] font-semibold text-amber-700">
                            {counts.awaiting} pending
                          </span>
                        </div>
                      </td>
                      <td className="hidden px-3 py-3 text-center tabular-nums text-emerald-700 sm:table-cell">
                        {counts.completed}
                      </td>
                      <td className="hidden px-3 py-3 text-center tabular-nums text-sky-700 sm:table-cell">
                        {counts.inProgress}
                      </td>
                      <td className="hidden px-3 py-3 text-center tabular-nums text-amber-700 sm:table-cell">
                        {counts.awaiting}
                      </td>
                      <td className="px-4 py-3 text-right font-medium tabular-nums text-slate-700">
                        {donePct}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDashboardCard;
